import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  IconButton,
  Collapse,
  useToast,
  Flex,
  Badge,
  useDisclosure,
} from "@chakra-ui/react";
import { ChatIcon, CloseIcon, SettingsIcon } from "@chakra-ui/icons";
import AIAssistant from "./AIAssistant";

/**
 * Persistent Chat Bubble Widget
 *
 * A floating chat bubble that stays at the bottom-right corner of every page.
 * Automatically syncs patient context when viewing patient profile pages.
 * Collapsible to minimize screen obstruction.
 *
 * Note: This component should be rendered inside a Portal from a parent container.
 * The parent container handles positioning and drag functionality.
 */
const ChatBubble = ({
  currentPatientId,
  currentPatientData,
  /** Med‑A plain text for AIAssistant (PatientPage emits after fetch). */
  documentContent = null,
  // Props from BubbleContainer for shared positioning
  isExpanded = false,
  onToggle = () => {},
  onClose = () => {},
  bubbleWidth = 400,
  bubbleHeight = 650,
  onHeaderMouseDown = () => {},
  hideCollapsedBubble = false,
  isResizing = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toast = useToast();

  // Patient context for the chat
  const [patientId, setPatientId] = useState(currentPatientId || null);
  const [patientData, setPatientData] = useState(currentPatientData || null);
  const lastPatientIdRef = useRef(null);

  // Sync state with parent if provided
  useEffect(() => {
    if (isExpanded !== undefined) {
      setIsOpen(isExpanded);
    }
  }, [isExpanded]);

  // Listen for patientContextUpdate events from PatientPage
  useEffect(() => {
    const handlePatientContextUpdate = (e) => {
      const { patientId: newPatientId, patientData: newPatientData } =
        e.detail || {};
      if (newPatientId && newPatientId !== lastPatientIdRef.current) {
        lastPatientIdRef.current = newPatientId;
        setPatientId(newPatientId);
        setPatientData(newPatientData || null);

        if (isOpen && newPatientId) {
          const patientName =
            newPatientData?.[0]?.patient_name ||
            newPatientData?.patient_name ||
            newPatientData?.name ||
            "Patient";
          console.log(
            `[ChatBubble] Patient context updated via event: ${patientName}`,
          );
        }
      }
    };

    window.addEventListener("patientContextUpdate", handlePatientContextUpdate);
    return () =>
      window.removeEventListener(
        "patientContextUpdate",
        handlePatientContextUpdate,
      );
  }, [isOpen]);

  // Sync patient context when props change
  useEffect(() => {
    if (currentPatientId && currentPatientId !== lastPatientIdRef.current) {
      lastPatientIdRef.current = currentPatientId;
      setPatientId(currentPatientId);
      setPatientData(currentPatientData || null);

      if (isOpen && currentPatientId) {
        const patientName =
          currentPatientData?.[0]?.patient_name ||
          currentPatientData?.patient_name ||
          currentPatientData?.name ||
          "Patient";
        console.log(`[ChatBubble] Patient context updated: ${patientName}`);
      }
    }
  }, [currentPatientId, currentPatientData, isOpen]);

  // Prevent drag event from firing on bubble click
  const handleBubbleMouseDown = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Toggle chat bubble open/closed
  const toggleChat = useCallback(
    (e) => {
      e.stopPropagation();
      const newState = !isOpen;
      setIsOpen(newState);
      if (onToggle) onToggle(newState);
    },
    [isOpen, onToggle],
  );

  // Close handler
  const handleClose = useCallback(
    (e) => {
      e.stopPropagation();
      setIsOpen(false);
      if (onClose) onClose();
    },
    [onClose],
  );

  // Collapsed bubble dimensions
  const collapsedSize = 60;

  return (
    <Box
      width={isOpen ? `${bubbleWidth}px` : `${collapsedSize}px`}
      height={isOpen ? `${bubbleHeight}px` : `${collapsedSize}px`}
      borderRadius={isOpen ? "lg" : "full"}
      bg="white"
      boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
      transition={isResizing ? "none" : "all 0.2s ease"}
      overflow="hidden"
      cursor="pointer"
      flexShrink={0}
      onMouseDown={handleBubbleMouseDown}
    >
      {isOpen ? (
        // Expanded Chat View
        <Flex direction="column" h="100%" className="chat-bubble-content">
          {/* Header */}
          <HStack
            bg="blue.600"
            color="white"
            px={4}
            py={3}
            justify="space-between"
            flexShrink={0}
            cursor="move"
            onMouseDown={onHeaderMouseDown}
          >
            <HStack spacing={2}>
              <ChatIcon />
              <Text fontWeight="semibold" fontSize="sm">
                AI Assistant
              </Text>
              {patientId && (
                <Badge colorScheme="green" fontSize="xs">
                  Active Patient
                </Badge>
              )}
            </HStack>
            <HStack spacing={1}>
              <IconButton
                icon={<CloseIcon />}
                size="sm"
                variant="ghost"
                color="white"
                _hover={{ bg: "blue.500" }}
                onClick={handleClose}
                aria-label="Close chat"
              />
            </HStack>
          </HStack>

          {/* AI Assistant Content */}
          <Box flex={1} overflow="hidden" className="ai-assistant-wrapper">
            <AIAssistant
              patientId={patientId}
              patientData={patientData}
              documentContent={documentContent}
              isMinimized={false}
              isGlobalPopup={false}
              onClose={handleClose}
              fullHeight={true}
            />
          </Box>
        </Flex>
      ) : hideCollapsedBubble ? null : (
        // Collapsed Bubble View
        <Box
          w="100%"
          h="100%"
          bg="blue.600"
          display="flex"
          alignItems="center"
          justifyContent="center"
          _hover={{ bg: "blue.500" }}
          transition="background-color 0.2s"
          onClick={toggleChat}
        >
          <ChatIcon color="white" boxSize={6} />
        </Box>
      )}
    </Box>
  );
};

export default ChatBubble;
